/**
 * NavigationServer - uniform interface for pathfinding engines. Selects backend
 * (Recast), exposes navmesh builders and path queries. Shields engine from implementation details.
 */

import { RecastNavigationServer } from './RecastNavigationServer.js';

import type { AssetPath } from '../storage/storageProvider.js';
import type * as THREE from 'three';

/**
 * Defines the type of navigation engine to use
 */
export enum NavigationEngine {
    Disable = 'disable',
    RecastNavigation = 'recast-navigation',
}

/**
 * Configuration options for navigation system
 */
export interface NavigationOptions {
    /** Navigation engine to use */
    engine: NavigationEngine;
    /** Debug mode for visualization */
    debug?: boolean;
    /** Misc. Options to be passed into the navigation engine */
    options?: any;
    /** Generate the navmesh on start up. */
    generateOnStartUp?: boolean;
}

/**
 * Options for configuring path following behavior
 */
export interface PathFollowingOptions {
    /** How close an agent needs to be to consider a waypoint reached (units) */
    waypointThreshold: number;
    /** Whether to loop the path when reaching the end */
    looped: boolean;
    /** How fast the agent should move along the path (units/second) */
    speed: number;
}

/**
 * Default path following options
 */
export const DEFAULT_PATH_FOLLOWING_OPTIONS: PathFollowingOptions = {
  waypointThreshold: 0.5,
  looped: false,
  speed: 1.0
};

/**
 * Result of a navigation query
 */
export interface NavigationQueryResult {
    /** Array of path points */
    path: THREE.Vector3[];
    /** Whether the path is complete (reaches the target) */
    complete: boolean;
}

export interface NavigationMeshGenerationInput {
    walkableMeshes: THREE.Mesh[];
    obstacleMeshes?: THREE.Mesh[];
}

/**
 * Interface for navigation engine implementations
 */
export interface INavigationServer {
    /**
     * Initialize the navigation engine
     * @param options Navigation configuration options
     */
    initialize(options: NavigationOptions): Promise<boolean>;
    destroy(): void;

    /**
     * Create a navigation mesh from a three.js mesh
     * @param mesh The mesh to create the zone from.
     * @returns True if the zone was created successfully, false otherwise
     */
    createNavigationMesh(mesh: THREE.Mesh): Promise<boolean>;

    /**
     * Create a navigation mesh from multiple meshes
     * @param meshes The meshes to create the navmesh from
     * @param overrideOptions Optional override options for the navigation mesh generation
     * @returns True if the navmesh was created successfully, false otherwise
     */
    createNavigationMeshFromMeshes(input: NavigationMeshGenerationInput, overrideOptions?: any): Promise<boolean>;

    /**
     * Set a navigation mesh directly
     * @param mesh The mesh to set as the navigation mesh
     */
    setNavigationMesh(navMesh: any): void;

    /**
     * Clear the current navigation mesh
     */
    clearNavigationMesh(): void;

    /**
     * Calculate a path between two points
     * @param start The starting point
     * @param end The ending point
     * @returns The path between the two points
     */
    calculatePath(
        start: THREE.Vector3,
        end: THREE.Vector3
    ): THREE.Vector3[];

    isPointOnNavigationMesh(point: THREE.Vector3): boolean;

    getClosestPointOnNavigationMesh(point: THREE.Vector3): THREE.Vector3;

    /**
     * Create debug visualization for the navigation mesh on demand
     * @returns A debug visualization object that can be added to the scene, or null if not available
     */
    createDebugVisualization(): THREE.Group | null;

    /**
     * Export the current navigation mesh to a binary file
     * @param scenePath The path to the scene file (used to determine navmesh file path)
     * @returns Promise resolving to export result with data and filename
     */
    exportNavigationMesh(scenePath: AssetPath): Promise<{ success: boolean; data?: ArrayBuffer; filename?: string }>;

    /**
     * Import a navigation mesh from a binary file
     * @param scenePath The path to the scene file (used to determine navmesh file path)
     * @returns Promise resolving to success/failure
     */
    importNavigationMesh(scenePath: AssetPath): Promise<boolean>;

    /**
     * Import a navigation mesh from binary data directly
     * @param navMeshData The NavMesh binary data as ArrayBuffer
     * @returns Promise resolving to success/failure
     */
    importNavigationMeshFromData(navMeshData: ArrayBuffer): Promise<boolean>;

    isReady(): boolean;

    /**
     * Merge generator options into the active config (editor navmesh bake passes UI values here).
     * Implemented by {@link RecastNavigationServer}; other engines may omit.
     */
    updateOptions?(partial: Record<string, unknown>): void;
}

export async function createNavigationServer(options: NavigationOptions): Promise<INavigationServer | null> {
  let server: INavigationServer | null = null;
  switch (options.engine) {
    case NavigationEngine.RecastNavigation:
      server = new RecastNavigationServer();
      break;
  }
  if (server) {
    await server.initialize(options);
  }
  return server;
}